import { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useEmblaCarousel from "embla-carousel-react";
import { baseFetch } from "../helpers/apiClient";
import { fetchProfileInfo } from "../redux/profileSlice";
import { useSnackbar } from "../context/SnackbarContext";

/**
 * Shared hook for subscription plan carousels.
 * Used by both edit-subscription screens (ChangePlan, ChangeSponsorPlan)
 * and create-profile plan selection screens.
 *
 * Consumers provide their own: handleChange, initial scroll logic, modals, and card body JSX.
 */
export default function usePlanCarousel() {
  const activePack = useSelector(
    (state) => state?.profileSlice?.profile?.purchaseInfo,
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState();
  const [packInfo, setPackInfo] = useState([]);
  const [showPayScreen, setShowPayScreen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [openPlansModal, setOpenPlansModal] = useState(false);
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  // Loose equality intentional — productId may be string or number depending on source
  const foundItem = packInfo?.find(
    (item) => activePack?.productId == item?.productId,
  );

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    containScroll: false,
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setActiveIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, onSelect]);

  // Embla needs reInit after plans load so it recalculates slide dimensions
  useEffect(() => {
    if (emblaApi && packInfo.length) {
      emblaApi.reInit();
    }
  }, [emblaApi, packInfo.length]);

  const handlePrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const handleNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    const getPlanInfo = () => {
      setLoading(true);
      baseFetch({
        url: "/v1/web/subscription/plans",
        method: "GET",
      })
        .then((response) => {
          if (response.status) {
            setPackInfo(response?.data?.plans);
          }
        })
        .catch((err) => console.error(err))
        .finally(() => {
          setLoading(false);
        });
    };
    getPlanInfo();
  }, []);

  // Accepts onSuccess callback so each consumer can close its own modal/state
  const handleCancelConfirm = useCallback(
    (onSuccess) => {
      setLoading(true);
      baseFetch({
        url: "/v1/web/subscription/cancel",
        method: "POST",
      })
        .then((response) => {
          if (response.status) {
            emblaApi?.scrollTo(0);
            setSelected(null);
            dispatch(fetchProfileInfo());
            onSuccess?.();
          }
        })
        .catch((err) => {
          showSnackbar({ message: err || "Request failed" });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [emblaApi, dispatch, showSnackbar],
  );

  const handleClosePayScreen = useCallback(async () => {
    await dispatch(fetchProfileInfo());
    setShowPayScreen(false);
  }, [dispatch]);

  return {
    emblaRef,
    emblaApi,
    activeIndex,
    handlePrev,
    handleNext,
    packInfo,
    loading,
    activePack,
    foundItem,
    handleCancelConfirm,
    showPayScreen,
    setShowPayScreen,
    handleClosePayScreen,
    selected,
    setSelected,
    selectedPlan,
    setSelectedPlan,
    openPlansModal,
    setOpenPlansModal,
  };
}
